import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sun, Moon, Search, LogOut, LayoutGrid, FolderKanban, ShieldCheck } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import kmuLogo from "../assets/kmu-logo.png";

export const Wordmark = ({ onDark = false }) => (
  <Link to="/" data-testid="wordmark-home-link" className="group flex items-center gap-3">
    <img
      src={kmuLogo}
      alt="Keimyung University — 계명대학교"
      className={onDark
        ? "h-11 w-auto rounded-md bg-white/95 px-3 py-1.5 shadow-lg transition-opacity duration-300 group-hover:opacity-90"
        : "h-11 w-auto transition-opacity duration-300 group-hover:opacity-85"}
    />
    <div className={`hidden font-mono text-[10px] font-medium uppercase tracking-[0.3em] sm:block ${onDark ? "text-blue-200/60" : "text-slate-400"}`}>
      AI Projects
    </div>
  </Link>
);

export const ThemeToggle = ({ onDark = false }) => {
  const { dark, toggle } = useTheme();
  return (
    <button
      data-testid="theme-toggle"
      onClick={toggle}
      aria-label="Toggle dark mode"
      className={`rounded-full border p-2 shadow-sm transition-all duration-300 ${
        onDark
          ? "border-white/20 bg-white/10 text-blue-100 hover:border-blue-300/60 hover:text-white"
          : "border-slate-200 bg-white text-slate-500 hover:border-[#2563EB]/50 hover:text-[#1E3A8A]"
      }`}
    >
      {dark ? <Sun size={14} /> : <Moon size={14} />}
    </button>
  );
};

export const LangToggle = ({ onDark = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div
      data-testid="language-toggle"
      className={`flex items-center rounded-full border p-0.5 shadow-sm ${
        onDark ? "border-white/20 bg-white/10" : "border-slate-200 bg-white"
      }`}
    >
      {[["en", "EN"], ["ko", "한"]].map(([code, label]) => (
        <button
          key={code}
          data-testid={`lang-${code}`}
          onClick={() => setLang(code)}
          className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-semibold tracking-wider transition-all duration-300 ${
            lang === code
              ? "bg-[#2563EB] text-white shadow"
              : onDark
                ? "text-blue-100/70 hover:text-white"
                : "text-slate-400 hover:text-slate-700"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

const initials = (name = "") => name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

export default function Header({ onSubmit }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLang();
  const { user, logout } = useAuth();

  const NAV = [
    { label: t.nav_home, to: "/", icon: LayoutGrid, key: "marketplace" },
    { label: t.nav_projects, to: "/projects", icon: FolderKanban, key: "projects" },
    ...(user?.role === "student" ? [{ label: t.nav_my_projects, to: "/my-projects", icon: FolderKanban, key: "my-projects" }] : []),
    ...(user?.role === "admin" ? [{ label: t.nav_admin, to: "/admin", icon: ShieldCheck, key: "admin" }] : []),
  ];

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const go = (to) => {
    setMenuOpen(false);
    navigate(to);
  };

  const active = (to) => location.pathname === to;
  const onDark = location.pathname === "/" && !scrolled && !menuOpen;

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        onDark
          ? "border-b border-transparent bg-transparent"
          : "border-b border-slate-200/80 bg-white/90 shadow-[0_2px_20px_rgba(15,23,42,0.05)] backdrop-blur-md"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-3.5 lg:px-10">
        <Wordmark onDark={onDark} />
        <nav className="hidden items-center gap-1 lg:flex" data-testid="main-nav">
          {NAV.map((item) => (
            <button
              key={item.key}
              data-testid={`nav-${item.key}`}
              onClick={() => go(item.to)}
              className={`relative px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                active(item.to)
                  ? onDark ? "text-white" : "text-[#1E3A8A]"
                  : onDark ? "text-blue-100/75 hover:text-white" : "text-slate-500 hover:text-slate-900"
              }`}
            >
              {item.label}
              {active(item.to) && (
                <motion.span layoutId="nav-underline" className="absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full bg-[#2563EB]" />
              )}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-2.5">
          <ThemeToggle onDark={onDark} />
          <LangToggle onDark={onDark} />
          <button
            data-testid="nav-search-button"
            onClick={() => go("/projects")}
            aria-label="Search projects"
            className={`hidden rounded-full border p-2 shadow-sm transition-colors sm:block ${
              onDark
                ? "border-white/20 bg-white/10 text-blue-100 hover:border-blue-300/60 hover:text-white"
                : "border-slate-200 bg-white text-slate-500 hover:border-[#2563EB]/50 hover:text-[#1E3A8A]"
            }`}
          >
            <Search size={14} />
          </button>
          {user ? (
            <div className="flex items-center gap-2" data-testid="user-menu">
              <span
                className={`hidden items-center gap-2 rounded-full border py-1.5 pl-1.5 pr-3.5 shadow-sm sm:flex ${
                  onDark ? "border-white/20 bg-white/10" : "border-slate-200 bg-white"
                }`}
                data-testid="user-chip"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#2563EB] font-mono text-[9px] font-bold text-white">
                  {initials(user.name)}
                </span>
                <span className={`max-w-[110px] truncate text-xs font-medium ${onDark ? "text-blue-50" : "text-slate-700"}`}>{user.name}</span>
              </span>
              <button
                data-testid="logout-button"
                onClick={() => { logout(); navigate("/"); }}
                aria-label="Logout"
                className={`rounded-full border p-2 shadow-sm transition-colors ${
                  onDark
                    ? "border-white/20 bg-white/10 text-blue-100 hover:border-rose-300/60 hover:text-rose-300"
                    : "border-slate-200 bg-white text-slate-500 hover:border-rose-300 hover:text-rose-500"
                }`}
              >
                <LogOut size={14} />
              </button>
            </div>
          ) : (
            <button
              data-testid="nav-login-button"
              onClick={() => go("/auth")}
              className={`rounded-md px-5 py-2.5 text-xs font-semibold transition-all duration-300 ${
                onDark ? "bg-white text-slate-900 hover:bg-blue-100" : "bg-slate-900 text-white hover:bg-[#1E3A8A]"
              }`}
            >
              {t.nav_login}
            </button>
          )}
          <button
            data-testid="mobile-menu-button"
            onClick={() => setMenuOpen(!menuOpen)}
            className={`rounded-full border p-2 shadow-sm lg:hidden ${
              onDark ? "border-white/20 bg-white/10 text-blue-100" : "border-slate-200 bg-white text-slate-600"
            }`}
            aria-label="Menu"
          >
            {menuOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-slate-200/80 bg-white/95 backdrop-blur-md lg:hidden"
            data-testid="mobile-nav"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {NAV.map((item) => (
                <button
                  key={item.key}
                  data-testid={`mobile-nav-${item.key}`}
                  onClick={() => go(item.to)}
                  className="rounded-lg px-3 py-3 text-left text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-[#1E3A8A]"
                >
                  {item.label}
                </button>
              ))}
              <button
                data-testid="mobile-submit-project-button"
                onClick={() => { setMenuOpen(false); onSubmit(); }}
                className="mt-2 rounded-md bg-[#1E3A8A] px-4 py-3 text-center text-sm font-semibold text-white"
              >
                {t.submit_cta}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
